import React, {
  CSSProperties,
  FC,
  ReactNode,
  MouseEventHandler,
  useState,
} from 'react';
import styles from './TextLink.module.css';

export enum TextLinkTargetType {
  SELF = '_self',
  BLANK = '_blank',
}

export interface TextLinkProps {
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;
  href?: string;
  target?: TextLinkTargetType;
  rel?: string;
  disabled?: boolean;
  linkStyle?: {
    sDefault?: CSSProperties;
    sHover?: CSSProperties;
    sFocus?: CSSProperties;
  };
  includeReferrer?: boolean;
  'aria-label'?: string;
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
}

const TextLink: FC<TextLinkProps> = ({
  href,
  children,
  target,
  rel,
  onClick,
  linkStyle,
  disabled,
  includeReferrer,
  'aria-label': ariaLabel,
  'aria-controls': ariaControls,
  'aria-expanded': ariaExpanded,
}) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);

  const isButton = onClick && !href;

  const textLinkTheme = {
    ...linkStyle?.sDefault,
    ...(hovered && linkStyle?.sHover),
    ...(focused && linkStyle?.sFocus),
  };

  const handleMouseEnter = () => {
    setHovered(true);
  };

  const handleMouseLeave = () => {
    setHovered(false);
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  if (isButton) {
    return (
      <button
        className={styles.textLink}
        style={textLinkTheme}
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-controls={ariaControls}
        aria-expanded={ariaExpanded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
      >
        {children}
      </button>
    );
  }

  return (
    <a
      className={styles.textLink}
      style={textLinkTheme}
      href={href}
      aria-disabled={disabled}
      target={target || TextLinkTargetType.BLANK}
      rel={
        (target || TextLinkTargetType.BLANK) === '_blank' && !includeReferrer
          ? `noopener noreferrer${rel ? ` ${rel}` : ''}`
          : rel
      }
      referrerPolicy={
        includeReferrer ? 'no-referrer-when-downgrade' : undefined
      }
      onClick={onClick}
      aria-label={ariaLabel}
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {children}
    </a>
  );
};

export default TextLink;
